import {
  ScrollHandler,
  debouncedSaveScrollPosition,
  debouncedScrollRestore,
} from '@repo/utils';
import { useEffect } from 'react';

const scrollHandler = ScrollHandler.instance;

type PathnameFunction<T> = () => string | T;
type PathnameFunctionKey<T> = keyof T;

type Options<T> = {
  containerRef?: React.RefObject<HTMLElement>;
  pathnameFunctionKey?: PathnameFunctionKey<T>;
  alwaysRestoreScroll?: boolean;
  disabled?: boolean;
};

export function useScrollMemo<T extends Record<any, any>>(
  pathnameFunction: PathnameFunction<T>,
  options: Options<T> = {},
) {
  const { pathnameFunctionKey, containerRef, alwaysRestoreScroll, disabled } =
    options;

  const pathnameResult = pathnameFunction();
  const pathname =
    typeof pathnameResult === 'string'
      ? pathnameResult
      : pathnameResult[pathnameFunctionKey ?? ''];

  useEffect(() => {
    const handlePopState = () => scrollHandler.setScrollRestoration();

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (disabled) return;

    function handleScroll(e: Event) {
      debouncedSaveScrollPosition(
        pathname,
        e.target instanceof HTMLElement ? e.target.scrollTop : window.scrollY,
      );
    }

    const container = containerRef?.current ?? window;

    debouncedScrollRestore(container, pathname, !!alwaysRestoreScroll);

    (containerRef?.current ?? document).addEventListener(
      'scroll',
      handleScroll,
    );

    return () => {
      (containerRef?.current ?? document).removeEventListener(
        'scroll',
        handleScroll,
      );
    };
  }, [pathname, containerRef, alwaysRestoreScroll, disabled]);
}
